import { forwardRef, useMemo } from 'react';

import compact from 'lodash/compact';

import {
  DrawerProps,
  ShadowDivider,
  Tab,
  Tabs,
  TabsProvider,
  Drawer,
  DrawerHeader,
  DrawerBody,
  DrawerContent,
  Typography,
  Stack,
  tokens,
} from '@cast/design-system';
import { DateRange } from '@cast/shared-types';

import { NetworkResourceType } from 'features/cluster/cost-report/network-cost/_types';
import { WorkloadDetailsRouteProps } from 'features/cluster/cost-report/workloads/_types';
import { useWorkloadTrafficDestinationsHistoryQuery } from 'hooks/queries/cost/useWorkloadTrafficDestinationsHistoryQuery';

import { DestinationsHistoryTable } from './DestinationsHistoryTable';
import { TopDestinationsChart } from './TopDestinationsChart';
import { TopDestinationsSummary } from './TopDestinationsSummary';

import { useCluster } from '@cast/shared-cluster-context-data-access';

import { FailedToLoad } from '@cast/shared-messages-ui';

import { Search, SearchProvider } from '@cast/shared-search-ui';

type Props = WorkloadDetailsRouteProps & {
  range: DateRange;
  adjustedRange: DateRange;
  timezone: string;
} & DrawerProps;

export const DestinationsHistoryDrawer = forwardRef<HTMLDivElement, Props>(
  (
    {
      workloadName,
      workloadType,
      namespace,
      range,
      adjustedRange,
      timezone,
      ...drawerProps
    }: Props,
    ref
  ) => {
    const { cluster } = useCluster();

    const { data, isLoading, error, refetch } =
      useWorkloadTrafficDestinationsHistoryQuery({
        clusterId: cluster.id,
        workloadName,
        workloadType,
        namespace,
        range: adjustedRange,
        limit: 0, // Unlimited
      });

    const topDestinations = useMemo(
      () => compact(data?.slice(0, 5).map((item) => item.destination) || []),
      [data]
    );

    return (
      <Drawer
        ref={ref}
        size="medium"
        {...drawerProps}
        testId="top-destinations-drawer"
      >
        <DrawerHeader />
        <DrawerBody>
          <DrawerContent>
            {error ? (
              <FailedToLoad
                title="Failed to load top destinations history"
                refresh={refetch}
              />
            ) : (
              <>
                <Stack gap={8}>
                  <Typography
                    variant="L12R"
                    color={tokens.theme.fg.structural.secondary}
                  >
                    CROSS-AZ COMMUNICATION
                  </Typography>
                  <Typography variant="h5">{workloadName}</Typography>
                </Stack>
                <TabsProvider initialTab={NetworkResourceType.COSTS}>
                  <Stack sx={{ mx: -32 }}>
                    <Tabs
                      fullWidth
                      size="small"
                      sx={{
                        '.MuiTabs-flexContainer': { paddingLeft: 32 },
                        mb: 0,
                        flexShrink: 0,
                      }}
                    >
                      <Tab value={NetworkResourceType.COSTS} label="Costs" />
                      <Tab
                        value={NetworkResourceType.TRAFFIC}
                        label="Traffic"
                      />
                    </Tabs>
                    <TopDestinationsSummary
                      data={topDestinations}
                      loading={isLoading}
                    />
                    <ShadowDivider opacity={0.2} size={16} />
                  </Stack>
                  <TopDestinationsChart
                    data={data}
                    range={range}
                    timezone={timezone}
                  />
                </TabsProvider>
                <SearchProvider data={data} isLoading={isLoading}>
                  <Search rootSx={{ pt: 32, pb: 16 }} />
                  <DestinationsHistoryTable />
                </SearchProvider>
              </>
            )}
          </DrawerContent>
        </DrawerBody>
      </Drawer>
    );
  }
);

DestinationsHistoryDrawer.displayName = 'DestinationsHistoryDrawer';
